import type {
  WidgetConfig,
  Message,
  AgentConfig,
  PendingApproval,
} from "../../shared/types";
import type { PlaybackState } from "../hooks/useVoiceOutput";
import type { VoiceInputState } from "../hooks/useVoiceInput";
import type { ConversationUIState } from "../types";
import { Header } from "./Header";
import { MessageList } from "./MessageList";
import { MessageInput } from "./MessageInput";

interface WidgetDisplayProps {
  widgetConfig: WidgetConfig;
  agentConfig: AgentConfig;
  messages: Message[];
  isLoading: boolean;
  statusMessage: string | null;
  conversationId: string | null;
  pendingApproval?: (PendingApproval & { error?: string }) | null;
  onSendMessage: (content: string) => Promise<void>;
  onQuickPromptSelect: (content: string) => Promise<void>;
  onRetryMessage: (messageId: string) => Promise<void>;
  onApprove?: (
    decision: "approved" | "denied",
    clientArtifacts?: Record<string, string>,
    toolResult?: string,
  ) => void;
  onSkip?: (toolApprovalId: string) => void;
  onClose: () => void;
  onReset: () => void;
  disableSendMessage?: boolean;
  voiceEnabled?: boolean;

  playbackState?: PlaybackState;
  voiceInputState?: VoiceInputState;
  isRecording?: boolean;
  onStartRecording?: () => void;
  onStopAndSubmit?: () => void;
  onSpeakMessage?: (messageId: string, text: string) => void;
  playingMessageId?: string | null;
  conversationUIState?: ConversationUIState;
  onExitConversationMode?: () => void;
}

export function WidgetDisplay({
  widgetConfig,
  agentConfig,
  messages,
  isLoading,
  statusMessage,
  conversationId,
  pendingApproval,
  onSendMessage,
  onQuickPromptSelect,
  onRetryMessage,
  onApprove,
  onSkip,
  onClose,
  onReset,
  disableSendMessage = false,
  voiceEnabled,

  playbackState,
  voiceInputState,
  isRecording,
  onStartRecording,
  onStopAndSubmit,
  onSpeakMessage,
  playingMessageId,
  conversationUIState,
  onExitConversationMode,
}: WidgetDisplayProps) {
  const primaryColor = agentConfig?.primaryColor || "#CC0020";
  const disclaimer =
    "By using this AI powered Chat, you agree to our [Terms of Use](https://www.civicplus.help/civicplus-help-center/docs/civicpluss-terms-of-use).";

  const playbackAnnouncement =
    playbackState === "loading"
      ? "Loading audio..."
      : playbackState === "playing"
        ? "Playing response"
        : playbackState === "error"
          ? "Audio unavailable"
          : "";

  return (
    <div
      className="flex h-full flex-col overflow-hidden rounded-xl border border-white/50 shadow-[0_7px_8px_0_rgba(56,56,56,0.16),0_5px_22px_0_rgba(56,56,56,0.09),0_12px_17px_0_rgba(56,56,56,0.11)]"
      role="region"
      aria-label="Chat widget"
    >
      <Header
        title={agentConfig?.agentTitle || agentConfig?.name}
        subtitle={agentConfig?.subheading || agentConfig?.subtitle}
        logoUrl={agentConfig?.iconUrl || undefined}
        primaryColor={primaryColor}
        onClose={onClose}
        onReset={onReset}
        isLoading={isLoading}
        conversationUIState={conversationUIState}
      />
      <div className="flex flex-1 flex-col overflow-hidden bg-[#FAFAFA]">
        <MessageList
          messages={messages}
          isLoading={isLoading}
          statusMessage={statusMessage}
          primaryColor={primaryColor}
          conversationId={conversationId}
          sourceUrl={widgetConfig.sourceUrl}
          agentId={widgetConfig.agentId}
          baseUrl={widgetConfig.apiUrl}
          organizationId={widgetConfig.organizationId}
          onRetryMessage={onRetryMessage}
          pendingApproval={pendingApproval}
          onApprove={onApprove}
          onSkip={onSkip}
          quickPrompts={agentConfig?.quickPrompts}
          onQuickPromptSelect={onQuickPromptSelect}
          voiceEnabled={voiceEnabled}
          onSpeakMessage={onSpeakMessage}
          playingMessageId={playingMessageId}
          playbackState={playbackState}
          conversationUIState={conversationUIState}
        />
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {playbackAnnouncement}
        </div>
        <MessageInput
          onSendMessage={onSendMessage}
          disabled={isLoading || !!pendingApproval}
          primaryColor={primaryColor}
          footerText={
            agentConfig?.footerText
              ? agentConfig.footerText + "\n\n" + disclaimer
              : disclaimer
          }
          disableSendMessage={disableSendMessage}
          voiceEnabled={voiceEnabled}
          playbackState={playbackState}
          voiceInputState={voiceInputState}
          isRecording={isRecording}
          onStartRecording={onStartRecording}
          conversationUIState={conversationUIState}
          onStopAndSubmit={onStopAndSubmit}
          onExitConversationMode={onExitConversationMode}
        />
      </div>
    </div>
  );
}
